import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useState } from "react";
import Service from "./../webservice/http";
import { useHistory } from "react-router-dom";
import { storage } from "../utils/storage";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const CXScoreTrendReport = () => {
  const history = useHistory();
  const services = new Service();

  const [activeFilter, setActiveFilter] = useState("7");

  const formatDMY = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  const [cxScoreData, setCXScoreData] = useState([]);
  const [cxScoreDates, setCXScoreDates] = useState([]);

  /**
   * Fetch collection score trend from API and populate chart
   */
  const fetchCollectionScoreTrend = async (fromDate, toDate) => {
    let url = "api/report/collection-score-trend/";
    if (fromDate && toDate) {
      url += `?from_date=${fromDate}&to_date=${toDate}`;
    }
    try {
      const res = await services.get(url);
      if (!res) return;
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      const items = res.date_wise_trend || [];
      setCXScoreDates(items.map((item) => item.date));
      setCXScoreData(items.map((item) => item.avg_score));
    } catch (e) {
      if (e.status === 401) {
        storage.clear();
        history.push("/login");
      } else {
        console.error("Collection score trend error:", e.message || e);
      }
    }
  };

  // Load chart on mount (default last 7 days)
  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    fetchCollectionScoreTrend(formatDMY(from), formatDMY(today));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Detailed calls table state (same as customer-intent.js) ───────────────
  const [callsData, setCallsData] = useState([]);
  const [callsOutcomes, setCallsOutcomes] = useState([]);
  const [callsProducts, setCallsProducts] = useState([]);
  const [callSearchText, setCallSearchText] = useState("");
  const [callProductFilter, setCallProductFilter] = useState("");
  const [callOutcomeFilter, setCallOutcomeFilter] = useState("");
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [callSelectionRange, setCallSelectionRange] = useState(null);

  const handleCallDateSelect = (ranges) => {
    setCallSelectionRange(ranges.selection);
  };

  const fetchDetailedCalls = async ({
    search,
    product,
    outcome,
    fromDate,
    toDate,
  } = {}) => {
    let url = "api/report/collection-info-detailed/";
    const parts = [];
    if (fromDate && toDate) {
      parts.push(`from_date=${fromDate}`);
      parts.push(`to_date=${toDate}`);
    }
    if (search) parts.push(`search=${encodeURIComponent(search)}`);
    if (product) parts.push(`product=${encodeURIComponent(product)}`);
    if (outcome) parts.push(`outcome=${encodeURIComponent(outcome)}`);
    if (parts.length) url += `?${parts.join("&")}`;

    const res = await services.get(url);
    if (!res || res === "TypeError: Failed to fetch") return;
    if (res.code === "token_not_valid") {
      storage.clear();
      history.push("/login");
      return;
    }
    setCallsData(res.data || []);
    setCallsOutcomes(res.filters?.outcomes || []);
    setCallsProducts(res.filters?.products || []);
  };

  // Load table data on mount (default last 7 days)
  useEffect(() => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    fetchDetailedCalls({ fromDate: formatDMY(from), toDate: formatDMY(today) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch table data when filters change
  useEffect(() => {
    const today = new Date();
    let fromDate, toDate;
    if (callSelectionRange) {
      fromDate = formatDMY(callSelectionRange.startDate);
      toDate = formatDMY(callSelectionRange.endDate);
    } else {
      const from = new Date(today);
      from.setDate(today.getDate() - parseInt(activeFilter || "7"));
      fromDate = formatDMY(from);
      toDate = formatDMY(today);
    }
    fetchDetailedCalls({
      search: callSearchText,
      product: callProductFilter,
      outcome: callOutcomeFilter,
      fromDate,
      toDate,
    });
  }, [
    callSearchText,
    callProductFilter,
    callOutcomeFilter,
    callSelectionRange,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Top-level filter (7/30/90 days) ──────────────────────────────────────
  const handleFilterClick = (filter) => {
    const today = new Date();
    const to = formatDMY(today);
    const f = new Date(today);
    f.setDate(today.getDate() - parseInt(filter));
    const from = formatDMY(f);

    setActiveFilter(filter);

    // Refresh both chart and table
    fetchCollectionScoreTrend(from, to);
    fetchDetailedCalls({
      fromDate: from,
      toDate: to,
      search: callSearchText,
      product: callProductFilter,
      outcome: callOutcomeFilter,
    });
  };

  // ─── Chart config ──────────────────────────────────────────────────────────
  const cxTrendOptions = {
    chart: {
      type: "scatter",
      height: (6 / 40) * 100 + "%",
    },
    title: { text: "" },
    subtitle: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: cxScoreDates,
      gridLineWidth: 0,
      title: { text: "Date" },
    },
    yAxis: {
      title: { text: "Avg Collection Score" },
      gridLineColor: "transparent",
      plotLines: [{ value: 0, width: 0, color: "#808080" }],
    },
    legend: { enabled: false },
    exporting: { enabled: false },
    plotOptions: { series: { lineWidth: 1 } },
    series: [{ name: "Avg Score", data: cxScoreData }],
  };

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />

      <div>
        <div className="container-fluid">
          <div className="my-call">
            <div
              className="side-panel-with-table clearfix"
              style={{ display: "flex" }}
            >
              <ReportNav />

              <div className="call-table">
                <div className="my-calls-column">
                  <div className="calls-top-pannel">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="pannel-heading clearfix">
                          <div className="pannel-heading-icon">
                            <i
                              className="fa fa-line-chart"
                              aria-hidden="true"
                            ></i>
                          </div>

                          <div className="pannel-heading-info">
                            <h3>Collection Score Trends</h3>
                            <p>
                              Track the Collection Score by date, and the impact
                              of Collection Score over time
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="pannel-nav clearfix">
                          <ul className="clearfix">
                            <div
                              className="btn-group btn-group-toggle"
                              data-toggle="buttons"
                            >
                              <label
                                className={`btn btn-default ${activeFilter === "7" ? "active" : ""}`}
                                id="7"
                                onClick={() => handleFilterClick("7")}
                              >
                                <input
                                  type="radio"
                                  name="options"
                                  autoComplete="off"
                                  style={{ outline: "none" }}
                                />{" "}
                                7 Days
                              </label>
                              <label
                                className={`btn btn-default ${activeFilter === "30" ? "active" : ""}`}
                                id="30"
                                onClick={() => handleFilterClick("30")}
                              >
                                <input
                                  type="radio"
                                  name="options"
                                  autoComplete="off"
                                  style={{ outline: "none" }}
                                />{" "}
                                30 Days{" "}
                              </label>
                              <label
                                className={`btn btn-default ${activeFilter === "90" ? "active" : ""}`}
                                id="90"
                                onClick={() => handleFilterClick("90")}
                              >
                                <input
                                  type="radio"
                                  name="options"
                                  autoComplete="off"
                                  style={{ outline: "none" }}
                                />{" "}
                                90 Days
                              </label>
                            </div>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Chart section */}
                  <div className="row">
                    <div
                      className="col-lg-12 col-md-12"
                      style={{ paddingBottom: "5px" }}
                    >
                      <div className="all-list" style={{ maxHeight: "300px" }}>
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={cxTrendOptions}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Detailed calls table section */}
                  <div
                    className="col-lg-12 bg-white"
                    style={{ marginTop: "8px", paddingBottom: "16px" }}
                  >
                    {/* Filter bar */}
                    <div
                      className="flex justify-end mb-4"
                      style={{
                        padding: "12px 0 0 0",
                        gap: "8px",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {/* Search */}
                      <input
                        type="text"
                        placeholder="Search..."
                        value={callSearchText}
                        onChange={(e) => setCallSearchText(e.target.value)}
                        className="search-tag"
                        style={{
                          border: "none",
                          background: "#eef2f5",
                          borderRadius: "10px",
                          width: "200px",
                          outline: "none",
                          padding: "10px",
                        }}
                      />

                      {/* Date Range Picker */}
                      <div style={{ position: "relative" }}>
                        <button
                          onClick={() => setDateRangeOpen((o) => !o)}
                          style={{
                            border: "none",
                            background: "#eef2f5",
                            borderRadius: "10px",
                            outline: "none",
                            padding: "10px 14px",
                            fontSize: "13px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {callSelectionRange
                            ? `${callSelectionRange.startDate.toLocaleDateString()} – ${callSelectionRange.endDate.toLocaleDateString()}`
                            : "Select Date Range"}
                        </button>
                        {dateRangeOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "44px",
                              right: 0,
                              zIndex: 999,
                              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          >
                            <DateRangePicker
                              ranges={[
                                callSelectionRange || {
                                  startDate: new Date(),
                                  endDate: new Date(),
                                  key: "selection",
                                },
                              ]}
                              onChange={handleCallDateSelect}
                            />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                                padding: "8px 12px",
                                background: "#fff",
                              }}
                            >
                              <button
                                onClick={() => {
                                  setCallSelectionRange(null);
                                  setDateRangeOpen(false);
                                }}
                                style={{
                                  border: "none",
                                  background: "#eef2f5",
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                Clear
                              </button>
                              <button
                                onClick={() => setDateRangeOpen(false)}
                                style={{
                                  border: "none",
                                  background: "#270086",
                                  color: "#fff",
                                  borderRadius: "6px",
                                  padding: "6px 12px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Product */}
                      <select
                        value={callProductFilter}
                        onChange={(e) => setCallProductFilter(e.target.value)}
                        style={{
                          border: "none",
                          background: "#eef2f5",
                          borderRadius: "10px",
                          outline: "none",
                          padding: "10px",
                          fontSize: "13px",
                        }}
                      >
                        <option value="">All Products</option>
                        {callsProducts.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>

                      {/* Outcome */}
                      <select
                        value={callOutcomeFilter}
                        onChange={(e) => setCallOutcomeFilter(e.target.value)}
                        style={{
                          border: "none",
                          background: "#eef2f5",
                          borderRadius: "10px",
                          outline: "none",
                          padding: "10px",
                          fontSize: "13px",
                        }}
                      >
                        <option value="">All Outcomes</option>
                        {callsOutcomes.map((outcome) => (
                          <option key={outcome.value} value={outcome.value}>
                            {outcome.display}
                          </option>
                        ))}
                      </select>

                      {/* Clear all */}
                      <button
                        onClick={() => {
                          setCallSearchText("");
                          setCallProductFilter("");
                          setCallOutcomeFilter("");
                          setCallSelectionRange(null);
                        }}
                        style={{
                          border: "none",
                          background: "#eef2f5",
                          borderRadius: "10px",
                          outline: "none",
                          padding: "10px",
                          fontSize: "13px",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-y-scroll">
                      <table className="ss">
                        <tbody>
                          <tr id="black-line">
                            {[
                              "Call Date",
                              "Customer",
                              "Agent",
                              "Language",
                              "Product",
                              "Outcome",
                              "Action",
                            ].map((h) => (
                              <th
                                key={h}
                                className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                              >
                                {h}
                              </th>
                            ))}
                          </tr>

                          {callsData.length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                style={{ textAlign: "center", padding: "20px" }}
                              >
                                No Record Found
                              </td>
                            </tr>
                          ) : (
                            callsData.map((call, i) => (
                              <tr key={i}>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-no-wrap text-left text-gray-900">
                                    {call.call_date}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-nowrap text-left text-gray-900">
                                    {call.customer_name}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-no-wrap text-left text-gray-900">
                                    {call.agent_name}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-no-wrap text-left text-gray-900">
                                    {call.language}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-no-wrap text-left text-gray-900">
                                    {call.product_name}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs">
                                  <p className="whitespace-nowrap text-left text-gray-900">
                                    {call.outcome_raw || "—"}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer">
                                  <a
                                    className="text-left text-gray-900 whitespace-nowrap"
                                    onClick={() =>
                                      call.call_id &&
                                      history.push(
                                        `/call/${call.call_id}/call-insight`,
                                      )
                                    }
                                  >
                                    View More
                                  </a>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CXScoreTrendReport;
